import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../api/axios";
import { PageContainer, PageTitleBadge, ModernButton } from "../components";
import { FaChartBar, FaArrowLeft } from "react-icons/fa";
import { useTranslation } from "../context/LanguageContext";
import "../styles/common.css";
import "./RapportTroupeau.css";

const RapportTroupeau = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [rapport, setRapport] = useState(null);
  const [troupeauData, setTroupeauData] = useState(null);
  const [users, setUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchRapport = async () => {
      try {
        // Récupérer le rapport, les données du troupeau, les utilisateurs et les catégories en parallèle
        const [rapportResponse, troupeauResponse, usersResponse, categoriesResponse] = await Promise.all([
          api.get(`/api/troupeaux/${id}/rapport/`),
          api.get(`/api/troupeaux/${id}/`),
          api.get("/api/users/"),
          api.get("/api/categories/")
        ]);
        
        setRapport(rapportResponse.data);
        setTroupeauData(troupeauResponse.data);
        setUsers(usersResponse.data);
        setCategories(categoriesResponse.data);
        setLoading(false);
      } catch (err) {
        console.error("Erreur de l'API:", err);
        console.error("ID utilisé:", id);
        console.error("Réponse d'erreur:", err.response);
        
        // Message d'erreur plus détaillé
        let errorMessage = t.errorLoadingHerdReport;
        if (err.response) {
          if (err.response.status === 404) {
            errorMessage = t.herdNotFound;
          } else if (err.response.status === 403) {
            errorMessage = t.noPermissionReport;
          } else if (err.response.status >= 500) {
            errorMessage = t.serverError;
          } else {
            errorMessage = err.response.data?.detail || err.response.data?.message || errorMessage;
          }
        } else if (err.request) {
          errorMessage = t.connectionError;
        }
        
        setError(errorMessage);
        setLoading(false);
      }
    };
    
    if (id) {
      fetchRapport();
    } else {
      setError(t.missingHerdId);
      setLoading(false);
    }
  }, [id]);

  // Fonction helper pour normaliser les noms de catégories (enlever accents, mettre en minuscule)
  const normalizeCategoryName = (name) => {
    if (!name) return "";
    return name
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "") // Enlever les accents
      .trim();
  };

  // Fonction helper pour trouver la valeur d'une catégorie dans le rapport
  const findCategoryValue = (categoryName, rapportData) => {
    if (!rapportData || !categoryName) return 0;
    
    // Essayer d'abord la correspondance exacte (sensible à la casse)
    if (rapportData[categoryName] !== undefined) {
      return rapportData[categoryName];
    }
    
    // Essayer la correspondance exacte insensible à la casse
    for (const key in rapportData) {
      if (key.toLowerCase() === categoryName.toLowerCase()) {
        return rapportData[key];
      }
    }
    
    // Normaliser le nom de la catégorie
    const normalizedCategory = normalizeCategoryName(categoryName);
    
    // Chercher dans toutes les clés du rapport avec matching flexible
    let bestMatch = null;
    let bestMatchScore = 0;
    
    for (const key in rapportData) {
      const normalizedKey = normalizeCategoryName(key);
      
      // Correspondance exacte après normalisation
      if (normalizedKey === normalizedCategory) {
        return rapportData[key];
      }
      
      // Correspondance partielle - calculer un score de similarité
      let score = 0;
      if (normalizedKey.includes(normalizedCategory) || normalizedCategory.includes(normalizedKey)) {
        // Plus la correspondance est longue, plus le score est élevé
        const minLength = Math.min(normalizedKey.length, normalizedCategory.length);
        const maxLength = Math.max(normalizedKey.length, normalizedCategory.length);
        score = minLength / maxLength;
        
        if (score > bestMatchScore) {
          bestMatchScore = score;
          bestMatch = rapportData[key];
        }
      }
    }
    
    // Si on a trouvé une correspondance avec un score raisonnable (> 0.5), l'utiliser
    if (bestMatch !== null && bestMatchScore > 0.5) {
      return bestMatch;
    }
    
    return 0;
  };

  // Fonction helper pour obtenir le nom du berger
  const getBergerName = () => {
    // Essayer depuis le rapport d'abord
    if (rapport?.Responsable) return rapport.Responsable;
    
    // Essayer depuis troupeauData
    if (troupeauData?.user) {
      const user = troupeauData.user;
      if (typeof user === 'object') {
        const parts = [];
        if (user.first_name) parts.push(user.first_name);
        if (user.last_name) parts.push(user.last_name);
        if (parts.length > 0) {
          return parts.join(" ");
        }
        if (user.username) return user.username;
      }
      // Si user est un ID, chercher dans la liste
      if (typeof user === 'number') {
        const foundUser = users.find(u => u.id === user);
        if (foundUser) {
          const parts = [];
          if (foundUser.first_name) parts.push(foundUser.first_name);
          if (foundUser.last_name) parts.push(foundUser.last_name);
          if (parts.length > 0) {
            return parts.join(" ");
          }
          if (foundUser.username) return foundUser.username;
        }
      }
    }
    
    return t.notAssigned;
  };

  if (loading) {
    return (
      <PageContainer>
        <div className="loading-container">
          <p className="loading-text">{t.loadingReport}</p>
        </div>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <PageTitleBadge title={t.error} icon={<FaChartBar />} />
        <div className="error-container">
          <p className="error-message">{error}</p>
        </div>
      </PageContainer>
    );
  }

  if (!rapport) {
    return (
      <PageContainer>
        <PageTitleBadge title={t.herdReportTitle} icon={<FaChartBar />} />
        <div className="empty-message-container">
          <p className="empty-message">{t.noReportAvailable}</p>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageTitleBadge title={`${t.reportTitle} ${rapport.Troupeau || t.herd}`} icon={<FaChartBar />} />

      {rapport && (
        <>
          {/* Informations générales */}
          <div className="rapport-section">
            <div className="info-cards">
              <div className="info-card">
                <span className="info-card-label">{t.shepherdName}</span>
                <span className="info-card-value">{getBergerName()}</span>
              </div>
              <div className="info-card valeur-card">
                <span className="info-card-label">{t.estimatedValueFull}</span>
                <span className="info-card-value valeur-amount">
                  {rapport["Valeur totale (FCFA)"] || "0"} FCFA
                </span>
              </div>
            </div>
          </div>

          {/* Indicateurs Clés */}
          <div className="rapport-section">
            <h2 className="section-title">{t.keyIndicators}</h2>
            <div className="table-container-modern">
              <table className="rapport-table">
                <thead>
                  <tr>
                    <th>{t.element}</th>
                    <th style={{ textAlign: "center" }}>{t.value}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{t.initialStock}</td>
                    <td style={{ textAlign: "center", fontWeight: "bold" }}>
                      {rapport["Effectif initial"] || 0}
                    </td>
                  </tr>
                  <tr>
                    <td>{t.births}</td>
                    <td style={{ color: "#10b981", fontWeight: "bold", textAlign: "center" }}>
                      {rapport.naissances || 0}
                    </td>
                  </tr>
                  <tr>
                    <td>{t.mortality}</td>
                    <td style={{ color: "#ef4444", fontWeight: "bold", textAlign: "center" }}>
                      {rapport.deces || 0}
                    </td>
                  </tr>
                  <tr>
                    <td>{t.purchases}</td>
                    <td style={{ color: "#3b82f6", fontWeight: "bold", textAlign: "center" }}>
                      {rapport.achats || 0}
                    </td>
                  </tr>
                  <tr>
                    <td>{t.sales}</td>
                    <td style={{ color: "#f59e0b", fontWeight: "bold", textAlign: "center" }}>
                      {rapport.ventes || 0}
                    </td>
                  </tr>
                  <tr>
                    <td>{t.zakat}</td>
                    <td style={{ color: "#8b5cf6", fontWeight: "bold", textAlign: "center" }}>
                      {rapport.zakat || 0}
                    </td>
                  </tr>
                  <tr className="total-row">
                    <td><strong>{t.calculatedTotal}</strong></td>
                    <td style={{ textAlign: "center", fontWeight: "bold" }}>
                      {(() => {
                        const initial = rapport["Effectif initial"] || 0;
                        const naissances = rapport.naissances || 0;
                        const achats = rapport.achats || 0;
                        const deces = rapport.deces || 0;
                        const ventes = rapport.ventes || 0;
                        const zakat = rapport.zakat || 0;
                        return initial + naissances + achats - deces - ventes - zakat;
                      })()}
                    </td>
                  </tr>
                  <tr className="total-row">
                    <td><strong>{t.currentStock}</strong></td>
                    <td style={{ textAlign: "center", fontWeight: "bold" }}>
                      {rapport["Nombre total d'animaux"] || 0}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Statistiques Naissances */}
          {rapport["Statistiques Naissances"] && categories.length > 0 && (
            <div className="rapport-section">
              <h2 className="section-title">{t.birthStatistics}</h2>
              <div className="table-container-modern">
                <table className="rapport-table">
                  <thead>
                    <tr>
                      <th>{t.indicator}</th>
                      <th style={{ textAlign: "center" }}>{t.value}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categories.map((cat) => {
                      const categoryName = cat.nom;
                      const naissancesStats = rapport["Statistiques Naissances"] || {};
                      // Mapper les catégories aux données du rapport
                      let value = 0;
                      let color = "#0f172a";
                      
                      // Vérifier d'abord si le rapport a une structure par catégorie
                      if (rapport["Statistiques Naissances par catégorie"] && rapport["Statistiques Naissances par catégorie"][categoryName]) {
                        value = rapport["Statistiques Naissances par catégorie"][categoryName] || 0;
                        color = "#3b82f6";
                      } else {
                        // Mapping des catégories aux valeurs du rapport (fallback pour compatibilité)
                        const categoryLower = categoryName.toLowerCase();
                        if (categoryLower.includes("veau") || categoryLower === "veaux") {
                          value = naissancesStats.Veaux || 0;
                          color = "#3b82f6";
                        } else if (categoryLower.includes("velle") || categoryLower === "velles") {
                          value = naissancesStats.Velles || 0;
                          color = "#10b981";
                        } else if (categoryLower.includes("vache") || categoryLower === "vaches") {
                          value = naissancesStats.Vaches || 0;
                          color = "#0f172a";
                        } else {
                          // Pour les autres catégories, chercher directement dans les données du rapport
                          value = naissancesStats[categoryName] || naissancesStats[categoryName + "s"] || 0;
                        }
                      }
                      
                      return (
                        <tr key={cat.id}>
                          <td>{t.numberOfAnimalsByCategory} {categoryName.toLowerCase()}</td>
                          <td style={{ color, fontWeight: "bold", textAlign: "center" }}>
                            {value}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Statistiques mortalité par catégorie */}
          {rapport["Statistiques mortalité par catégorie"] && categories.length > 0 && (
            <div className="rapport-section">
              <h2 className="section-title">{t.mortalityStatistics}</h2>
              <div className="table-container-modern">
                <table className="rapport-table">
                  <thead>
                    <tr>
                      <th>{t.category}</th>
                      <th style={{ textAlign: "center" }}>{t.total}</th>
                      <th style={{ textAlign: "center" }}>{t.deaths}</th>
                      <th style={{ textAlign: "center" }}>{t.mortalityRate}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categories.map((cat) => {
                      const categoryName = cat.nom;
                      const mortaliteStats = rapport["Statistiques mortalité par catégorie"] || {};
                      // Trouver les stats pour cette catégorie avec matching flexible
                      let stats = mortaliteStats[categoryName];
                      if (!stats) {
                        // Chercher avec matching flexible
                        const normalizedCategory = normalizeCategoryName(categoryName);
                        for (const key in mortaliteStats) {
                          const normalizedKey = normalizeCategoryName(key);
                          if (normalizedKey === normalizedCategory || 
                              normalizedKey.includes(normalizedCategory) || 
                              normalizedCategory.includes(normalizedKey)) {
                            stats = mortaliteStats[key];
                            break;
                          }
                        }
                      }
                      stats = stats || { Total: 0, Morts: 0, "Taux de mortalité (%)": 0 };
                      return (
                        <tr key={cat.id}>
                          <td>{categoryName}</td>
                          <td style={{ textAlign: "center" }}>{stats.Total}</td>
                          <td style={{ fontWeight: "bold", textAlign: "center" }}>
                            {stats.Morts}
                          </td>
                          <td style={{ textAlign: "center" }}>
                            {Math.round(stats["Taux de mortalité (%)"])}
                          </td>
                        </tr>
                      );
                    })}
                    <tr className="total-row">
                      <td><strong>{t.global}</strong></td>
                      <td style={{ textAlign: "center" }}>
                        {Object.values(rapport["Statistiques mortalité par catégorie"]).reduce((sum, c) => sum + c.Total, 0)}
                      </td>
                      <td style={{ textAlign: "center", fontWeight: "bold" }}>
                        {Object.values(rapport["Statistiques mortalité par catégorie"]).reduce((sum, c) => sum + c.Morts, 0)}
                      </td>
                      <td style={{ textAlign: "center" }}>
                        {Math.round(rapport["Taux de mortalité (%)"] || 0)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Répartition par catégorie */}
          {rapport["Répartition par catégorie"] && categories.length > 0 && (
            <div className="rapport-section">
              <h2 className="section-title">{t.categoryDistribution}</h2>
              <div className="table-container-modern">
                <table className="rapport-table">
                  <thead>
                    <tr>
                      <th>{t.category}</th>
                      <th style={{ textAlign: "center" }}>{t.stockCount}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categories.map((cat) => {
                      const categoryName = cat.nom;
                      const rapportCategories = rapport["Répartition par catégorie"] || {};
                      const count = findCategoryValue(categoryName, rapportCategories);
                      return (
                        <tr key={cat.id}>
                          <td>{categoryName}</td>
                          <td style={{ textAlign: "center" }}>{count}</td>
                        </tr>
                      );
                    })}
                    <tr className="total-row">
                      <td><strong>{t.total}</strong></td>
                      <td style={{ textAlign: "center", fontWeight: "bold" }}>
                        {(() => {
                          const total = Object.values(rapport["Répartition par catégorie"]).reduce((sum, count) => sum + (count || 0), 0);
                          return total || rapport["Nombre total d'animaux"] || 0;
                        })()}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Bouton retour */}
      <div className="actions-container">
        <ModernButton
          variant="secondary"
          icon={<FaArrowLeft />}
          onClick={() => navigate("/troupeaux")}
        >
          {t.back}
        </ModernButton>
      </div>
    </PageContainer>
  );
};

export default RapportTroupeau;
